import { ChangeDetectionStrategy, Component, inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import { CapturumButtonModule } from '@capturum/ui/button';
import { Parameter } from '@features/applications/interfaces/action.interface';
import { FieldArrayType, FormlyModule } from '@ngx-formly/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ConfirmationService } from 'primeng/api';

@Component({
  selector: 'app-formly-application-action-parameters-repeat',
  imports: [FormlyModule, CapturumButtonModule, TranslateModule],
  templateUrl: './formly-application-action-parameters-repeat.component.html',
  styleUrl: './formly-application-action-parameters-repeat.component.scss',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyApplicationActionParametersRepeatComponent extends FieldArrayType implements OnInit {
  public applicationId: string;
  public actionId: string;
  public addDisabled = signal<boolean>(false);

  private confirmationService = inject(ConfirmationService);
  private translateService = inject(TranslateService);

  public ngOnInit(): void {
    this.applicationId = this.props.applicationId;
    this.actionId = this.props.actionId;

    this.formControl.disable();
  }

  public override remove(index: number): void {
    this.confirmationService.confirm({
      header: this.translateService.instant('confirm.title'),
      message: this.translateService.instant('confirm.entity.delete.confirmation', {}),
      accept: () => {
        if (this.props.onRemove) {
          const formControl = this.formControl.at(index);
          const id = formControl.get('id')?.value;

          this.props.onRemove(id, this.field, formControl, () => {
            super.remove(index);
          });
        } else {
          super.remove(index);
        }
      },
    });
  }

  public override add(): void {
    const index = this.field.fieldGroup.length;

    if (this.props.onAdd) {
      super.add(index);

      this.props.onAdd(this.field, index, () => {
        this.formControl.at(index).patchValue({
          is_edit: true,
        });
        this.addDisabled.set(true);
      });
    } else {
      super.add(index);
      this.formControl.at(index).patchValue({
        is_edit: true,
      });
      this.addDisabled.set(true);
    }
  }

  public edit(index: number): void {
    if (this.props.onEdit) {
      this.props.onEdit(index, this.field);
    } else {
      this.addDisabled.set(true);
      this.formControl.at(index).enable();
      this.formControl.at(index).patchValue({
        is_edit: true,
      });

      for (const control of this.formControl.controls) {
        if (control !== this.formControl.at(index)) {
          control.disable();
          control.patchValue({
            is_edit: false,
          });
        }
      }
    }
  }

  public save(index: number): void {
    if (this.props.onSave) {
      const formControl = this.formControl.at(index);

      this.props.onSave(index, formControl, (parameter: Parameter) => {
        this.addDisabled.set(false);
        formControl.patchValue({
          is_edit: false,
          ...parameter,
        });
        formControl.disable();
      });
    }
  }

  public cancel(index: number): void {
    if (this.props.onCancel) {
      this.props.onCancel(index, this.field, this.formControl);
    } else {
      if (this.formControl.at(index).get('id')?.value) {
        this.formControl.at(index).disable();
        this.formControl.at(index).patchValue({
          is_edit: false,
        });
      } else {
        super.remove(index);
      }

      this.addDisabled.set(false);
    }
  }
}
